import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ApiError,
  IMAGE_SHAPES,
  getBookPictures,
  getCharacters,
  type BookPicture,
  type CropRegion,
  type ImageCandidate,
  type ImageShape,
  type ImageQuality,
  type DrawPrice,
  type PictureDescription,
  type Portrait,
} from "../api";
import ChipRow from "../components/ChipRow";
import SheetHeader from "../components/SheetHeader";
import { SendIcon } from "../components/icons";
import { useT, type Messages } from "../i18n";
import Cropper from "./Cropper";
import { aroundAmount, formatMicros } from "./money";

/**
 * PictureSheet is how any picture gets made: a scene for a chapter (ADR-0038),
 * a character's reference portrait (ADR-0039), or a book's cover (ADR-0041).
 *
 * The order is the whole design. **Words first, then pixels**: a description
 * is written from the passage for a fraction of a cent, the author reads it
 * and edits it as text, and only then is a picture drawn for about seven
 * cents. Iterating on words is nearly free and iterating on pictures is not,
 * so the cheap loop is the one placed under the thumb.
 *
 * Nothing here changes the chapter until **Use this one**: drawn pictures are
 * candidates the object store holds and the garbage collector clears a week
 * later, so trying four and keeping one costs four pictures and leaves one
 * row — the same bargain a rewrite conversation strikes (ADR-0025).
 *
 * One component for all three because the loop is the same loop, and they
 * would otherwise drift: the same tabs, the same editable brief, the same
 * gallery, the same test ids. What differs is the endpoints, the line quoted
 * at the top, and whether a shape can be chosen — a portrait is always square,
 * because its shape would become every scene's shape, and a cover is always
 * upright, because a library of mixed shapes is a library of ragged rows.
 */
/** PictureKind is which of the three briefs a sheet is running. */
export type PictureKind = "scene" | "portrait" | "cover";

/**
 * What differs between the three kinds, in one table.
 *
 * Three nested ternaries on `kind` had grown in four places — the empty state,
 * the Suggest button, the placeholder, and whether a shape can be chosen — and
 * a fourth kind would have added four more. A table is the honest shape: the
 * flow is identical and only these five facts are not.
 */
interface KindCopy {
  empty: (t: Messages) => string;
  suggest: (t: Messages) => string;
  placeholder: (t: Messages) => string;
  /** Whether the author picks the shape. A portrait is always square because
   *  its shape is inherited by every scene drawn from it; a cover is always
   *  upright because a library of mixed shapes has ragged rows. */
  choosesShape: boolean;
  /** Whether the picture has a cast the author sees and edits (ADR-0047). A
   *  portrait is of one person and is drawn from nobody else. */
  hasCast: boolean;
  /** Whether earlier pictures of the book may be sent with it, for the place
   *  rather than the faces (ADR-0072). A portrait has no place — it is one
   *  person against nothing, and a reference with a room in it would put that
   *  room into every scene drawn from the portrait afterwards. */
  hasContinuity: boolean;
  /** Whether its cast may show a character in one of their forms (ADR-0159).
   *  A scene only: a cover naming a form waits for the owner to ask. */
  hasForms: boolean;
  /** The shape a crop is held to (ADR-0048), or null for the author's choice;
   *  the server holds it to the same one. */
  cropShape: string | null;
  lockedNote?: (t: Messages) => string;
}

const KINDS: Record<PictureKind, KindCopy> = {
  scene: {
    empty: (t) => t.images.empty,
    suggest: (t) => t.images.suggest,
    placeholder: (t) => t.images.placeholder,
    choosesShape: true,
    hasCast: true,
    hasContinuity: true,
    hasForms: true,
    cropShape: null,
  },
  portrait: {
    empty: (t) => t.images.portraitEmpty,
    suggest: (t) => t.images.portraitSuggest,
    placeholder: (t) => t.images.portraitPlaceholder,
    choosesShape: false,
    hasCast: false,
    hasContinuity: false,
    hasForms: false,
    cropShape: "1:1",
    lockedNote: (t) => t.crop.lockedSquare,
  },
  cover: {
    empty: (t) => t.images.coverEmpty,
    suggest: (t) => t.images.coverSuggest,
    placeholder: (t) => t.images.coverPlaceholder,
    choosesShape: false,
    hasCast: true,
    hasContinuity: false,
    hasForms: false,
    cropShape: "3:4",
    lockedNote: (t) => t.crop.lockedCover,
  },
};

export default function PictureSheet({
  kind,
  title,
  subject,
  prices,
  canDescribe,
  onDescribe,
  onGenerate,
  onUpload,
  onCrop,
  onUse,
  onClose,
  charactersHref,
  castBookID,
  current,
  live,
}: {
  /** Which brief this is: it changes only the words, not the flow. */
  kind: PictureKind;
  title: string;
  /** The passage, or the character — what the picture is *of*. */
  subject: string;
  /**
   * What one picture costs at each quality the server offers, formatted by it
   * (ADR-0086). One chip per entry, and the price of the chosen one on the
   * button. A single entry means this server offers no choice, and no chips
   * are shown at all.
   */
  prices: DrawPrice[];
  /**
   * Whether this account may spend on a description and a picture. Without it
   * the sheet offers uploading only, rather than a tab that answers 403.
   */
  canDescribe: boolean;
  onDescribe: (v: {
    current: string;
    earlier: string[];
    instruction: string;
    /** Only for a kind with a cast: the server refuses fields it does not know. */
    character_ids?: string[];
    /** The forms the cast is shown in (ADR-0159); only with character_ids. */
    form_ids?: string[];
    /** Only for a kind that carries continuity (ADR-0072). */
    reference_image_ids?: string[];
  }) => Promise<PictureDescription>;
  /**
   * Draws the picture from the words, the cast and the earlier pictures it is
   * being drawn beside, all by id.
   */
  onGenerate: (
    prompt: string,
    shape: ImageShape,
    cast: string[],
    beside: string[],
    quality: ImageQuality,
    /** The forms the cast is drawn in (ADR-0159), each from its own face. */
    forms: string[],
  ) => Promise<ImageCandidate>;
  onUpload: (file: Blob) => Promise<ImageCandidate>;
  /** Cuts part of a stored picture into a new candidate (ADR-0048). Free. */
  onCrop: (objectKey: string, region: CropRegion) => Promise<ImageCandidate>;
  /**
   * Attaches a candidate. A crop of the picture in force comes through here
   * too, carrying that picture's description.
   */
  onUse: (candidate: ImageCandidate) => Promise<unknown>;
  onClose: () => void;
  /**
   * Where the book's characters are, so a description naming somebody with no
   * face can send the author to draw them first (ADR-0044).
   */
  charactersHref?: string;
  /**
   * The book whose characters a scene or cover may be drawn from, so the
   * author can add one to the cast (ADR-0047).
   */
  castBookID?: string;
  /**
   * The picture already in force, if any. Shown at the top so the author sees
   * what they are replacing, and its description is where the conversation
   * starts, so "older" changes that face rather than starting a new one.
   */
  current?: Pick<Portrait, "url" | "object_key" | "width" | "height" | "prompt"> & {
    /** Who it was drawn from, so the conversation starts with them (ADR-0053). */
    character_ids?: string[];
    /** The forms they were drawn in (ADR-0159). */
    form_ids?: string[];
  };
  /**
   * The picture brought to life, beside the one in force (ADR-0127): a
   * portrait's live portrait, running. Only a portrait has one.
   */
  live?: ReactNode;
}) {
  const t = useT();
  const [tab, setTab] = useState<"describe" | "upload">(canDescribe ? "describe" : "upload");
  // The brief. Editable text, not a transcript: it is what gets sent to the
  // picture model verbatim, so the author has to be able to change a word in
  // it without asking a model to do it for them.
  const [description, setDescription] = useState(current?.prompt?.trim() ?? "");
  const [instruction, setInstruction] = useState("");
  // Whether the last turn rewrote the description. The one thing the old sheet
  // never said: the new words land in a textarea that is above the fold on a
  // phone, so a successful turn looked identical to nothing happening.
  const [updated, setUpdated] = useState(false);
  // Once there is a picture the picture is the page (requirement 0030): the
  // brief folds to a line that opens on a tap, and the shape picker folds with
  // it, because a shape is chosen before the first draw and almost never after.
  const [briefOpen, setBriefOpen] = useState(true);
  const [earlier, setEarlier] = useState<string[]>([]);
  const [shape, setShape] = useState<ImageShape>("4:3");
  // **Standard every time this sheet opens** (ADR-0086), including straight
  // after one that drew with the expensive model: nothing remembers the last
  // choice, because a default nobody chose is what this exists to avoid. The
  // sheet is unmounted when it closes, so this state is the whole mechanism.
  const [quality, setQuality] = useState<ImageQuality>("standard");
  const chosenPrice = prices.find((p) => p.quality === quality) ?? prices[0];
  const [candidates, setCandidates] = useState<ImageCandidate[]>([]);
  const [chosen, setChosen] = useState<ImageCandidate | null>(null);
  const [spentMicros, setSpentMicros] = useState(0);
  // Who is in the picture, by character id (ADR-0047): the describe turn's
  // answer, then whatever the author adds or takes out. It is sent with the
  // draw, and it is the only thing that decides whose portraits go with it —
  // the words are not searched for names, because a description in another
  // script than the roster spells them differently.
  const [cast, setCast] = useState<string[]>(current?.character_ids ?? []);
  // The forms they are shown in (ADR-0159), beside the cast and checked
  // against it: a form whose character left the cast leaves with them.
  const [forms, setForms] = useState<string[]>(current?.form_ids ?? []);
  // Names the server gave for the cast, for before the roster has loaded, and
  // which of them had no portrait then.
  const [castNames, setCastNames] = useState<Record<string, string>>({});
  // The earlier pictures this one is being drawn beside (ADR-0072): the place,
  // not the people. Chosen by the author, at most two, and sent to both calls —
  // their words to the brief, their pixels to the picture model.
  const [beside, setBeside] = useState<string[]>([]);
  const [serverNeedsFaces, setServerNeedsFaces] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  // A refusal whose remedy is on the Characters screen: a face to draw, or a
  // cast to read. A description edited by hand can name somebody the last
  // turn did not, so the server's answer is the one that counts.
  const [problemIsCast, setProblemIsCast] = useState(false);
  // What is being cropped: a candidate in the gallery, or the picture in force.
  const [cropping, setCropping] = useState<
    { of: "candidate"; candidate: ImageCandidate } | { of: "current" } | null
  >(null);
  const body = useRef<HTMLDivElement>(null);
  const client = useQueryClient();

  // The book's characters, for adding one and for knowing who has a face.
  // Same key as the Characters screen, so a portrait drawn there is seen here.
  const hasCast = KINDS[kind].hasCast && !!castBookID;
  const roster = useQuery({
    queryKey: ["characters", castBookID, "draft"],
    queryFn: () => getCharacters(castBookID!),
    enabled: hasCast,
    retry: false,
  });
  const people = roster.data?.characters;

  // Every picture the book already has. Fetched only for a kind that can carry
  // one, and only once the sheet is open — an author who never scrolls to the
  // row never asks for it.
  const hasContinuity = KINDS[kind].hasContinuity && !!castBookID;
  const gallery = useQuery({
    queryKey: ["book-pictures", castBookID],
    queryFn: () => getBookPictures(castBookID!),
    enabled: hasContinuity,
    retry: false,
  });
  const earlierPictures = gallery.data?.pictures ?? [];
  // A picture deleted since it was ticked is out of it, like a deleted
  // character is out of the cast.
  const liveBeside = gallery.data
    ? beside.filter((id) => earlierPictures.some((p) => p.id === id))
    : beside;
  // A character deleted since the description was written is out of it.
  const liveCast = people ? cast.filter((id) => people.some((c) => c.id === id)) : cast;
  const hasForms = hasCast && KINDS[kind].hasForms;
  const liveForms = !hasForms
    ? []
    : people
      ? forms.filter((f) =>
          people.some((c) => liveCast.includes(c.id) && (c.forms ?? []).some((x) => x.id === f)),
        )
      : forms;
  const members = liveCast.map((id) => {
    const person = people?.find((c) => c.id === id);
    const form = person?.forms?.find((f) => liveForms.includes(f.id));
    // A form is its own face: never the character's in its place (ADR-0159).
    if (form) return { id: `${id}/${form.id}`, name: form.name, faceless: !form.portrait };
    return { id, name: person?.name ?? castNames[id] ?? "", faceless: person && !person.portrait };
  });
  // The roster as the cast picker offers it: each character, then each of
  // their forms, keyed "character/form" so one choice is both.
  const offered = (people ?? []).flatMap((c) => [
    { id: c.id, name: c.name },
    ...(hasForms ? (c.forms ?? []) : []).map((f) => ({
      id: `${c.id}/${f.id}`,
      name: t.characters.forms.of(f.name, c.name),
    })),
  ]);
  const chooseCast = (keys: string[]) => {
    setCast(keys.map((k) => k.split("/")[0] ?? k));
    setForms(keys.flatMap((k) => (k.includes("/") ? [k.split("/")[1] ?? ""] : [])));
  };
  // Worked out here once the roster is in, because the author edits the cast;
  // the server's answer covers the moment before.
  const needsFaces = people
    ? members.filter((m) => m.faceless).map((m) => m.name)
    : serverNeedsFaces;

  const message = (err: unknown, fallback: string) =>
    err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : fallback;

  const describe = useMutation({
    mutationFn: (v: { instruction: string }) =>
      onDescribe({
        current: description,
        earlier,
        instruction: v.instruction,
        ...(hasCast && { character_ids: liveCast }),
        ...(hasForms && { form_ids: liveForms }),
        ...(hasContinuity && { reference_image_ids: liveBeside }),
      }),
    onSuccess: (result, v) => {
      setDescription(result.text);
      // The words and the cast are one answer, so both are replaced.
      const ids = result.character_ids ?? [];
      setCast(ids);
      setForms(result.form_ids ?? []);
      setCastNames((known) => {
        const next = { ...known };
        ids.forEach((id, i) => {
          next[id] = result.characters?.[i] ?? next[id] ?? "";
        });
        return next;
      });
      setServerNeedsFaces(result.needs_faces ?? []);
      // Kept so the next turn can be a follow-up — "warmer", then "and at
      // dawn" — rather than starting the brief again.
      if (v.instruction) setEarlier((all) => [...all, v.instruction]);
      setSpentMicros((n) => n + result.cost_micros);
      setUpdated(true);
    },
    onError: (err, v) => {
      setProblem(message(err, t.images.describeFailed));
      setInstruction((current) => current || v.instruction);
    },
  });

  // An override, because "Update and draw" draws the words the describe turn
  // has *just* returned: setDescription/setCast are queued, so reading state
  // here would draw the previous brief with the previous cast — the exact
  // "did it use my change?" doubt this button exists to remove.
  const draw = useMutation({
    mutationFn: (override?: { prompt: string; cast: string[]; forms: string[] }) =>
      onGenerate(
        (override?.prompt ?? description).trim(),
        shape,
        override?.cast ?? liveCast,
        liveBeside,
        quality,
        override?.forms ?? liveForms,
      ),
    onSuccess: (candidate) => {
      setCandidates((all) => [...all, candidate]);
      setChosen(candidate);
      setSpentMicros((n) => n + candidate.cost_micros);
      // The picture is now the thing being judged; the brief gets out of its
      // way until it is asked for.
      setBriefOpen(false);
    },
    onError: (err) => {
      setProblem(message(err, t.images.drawFailed));
      const conflict = err instanceof ApiError && err.status === 409;
      setProblemIsCast(conflict);
      // A character was deleted or lost their face: read the roster again.
      if (conflict && hasCast) {
        void client.invalidateQueries({ queryKey: ["characters", castBookID, "draft"] });
      }
    },
  });

  const upload = useMutation({
    mutationFn: (file: Blob) => onUpload(file),
    onSuccess: (candidate) => {
      setCandidates((all) => [...all, candidate]);
      setChosen(candidate);
    },
    onError: (err) => setProblem(message(err, t.images.uploadFailed)),
  });

  const crop = useMutation({
    mutationFn: async (region: CropRegion) => {
      const from = cropping?.of === "candidate" ? cropping.candidate : null;
      const key = from ? from.object_key : current?.object_key;
      if (!key) throw new Error(t.crop.failed);
      const cut = await onCrop(key, region);
      // A crop is the same picture, smaller: it keeps the words it was drawn
      // from and whose faces it used, and costs nothing.
      const kept: ImageCandidate = from
        ? {
            ...cut,
            prompt: from.prompt,
            character_ids: from.character_ids,
            form_ids: from.form_ids,
            drawn_from: from.drawn_from,
            cost_micros: 0,
          }
        : {
            ...cut,
            prompt: current?.prompt,
            character_ids: current?.character_ids ?? [],
            form_ids: current?.form_ids ?? [],
            cost_micros: 0,
          };
      if (!from) await onUse(kept);
      return kept;
    },
    onSuccess: (kept) => {
      if (cropping?.of === "candidate") {
        setCandidates((all) => [...all, kept]);
        setChosen(kept);
      }
      setCropping(null);
    },
  });

  const place = useMutation({
    mutationFn: (candidate: ImageCandidate) => onUse(candidate),
    onError: (err) => setProblem(message(err, t.images.placeFailed)),
  });

  const sendInstruction = () => {
    const text = instruction.trim();
    // An empty instruction on the first turn is "suggest something": the model
    // has the passage, which is enough to start from, and asking the author to
    // write a prompt before they have seen one is the blank page again.
    if (busy) return;
    setProblem(null);
    setUpdated(false);
    setInstruction("");
    describe.mutate({ instruction: text });
  };

  /**
   * The primary button (requirement 0030).
   *
   * With words waiting in the box it is **one press, two paid calls**: rewrite
   * the description, then draw the rewrite. That is what the author means by
   * typing a change and reaching for the picture button, and the old sheet
   * silently ignored the box — it redrew the description as it stood, which is
   * indistinguishable from the change having been thrown away.
   *
   * ADR-0038's order is untouched: the words still come first and are still
   * shown before the pixels are paid for. What changes is that pressing one
   * thing does not leave the other half undone. The price on the button and the
   * running total under it both report two calls.
   */
  const drawNow = async () => {
    const text = instruction.trim();
    setProblem(null);
    setProblemIsCast(false);
    if (text === "") {
      setUpdated(false);
      draw.mutate(undefined);
      return;
    }
    setInstruction("");
    let written: PictureDescription;
    try {
      written = await describe.mutateAsync({ instruction: text });
    } catch {
      // describe's own onError has said why and put the words back in the box.
      // Nothing is drawn: paying seven cents for the brief the author was in
      // the middle of changing is the worse of the two failures.
      return;
    }
    setUpdated(true);
    draw.mutate({
      prompt: written.text,
      cast: written.character_ids ?? [],
      forms: written.form_ids ?? [],
    });
  };

  // Go to the picture when one arrives. This used to scroll to the bottom,
  // because the gallery was the last thing in the sheet; it is the first thing
  // now (requirement 0030), so the same intent is the opposite scroll.
  //
  // A **layout** effect, not a passive one: passive effects run after paint, so
  // the sheet painted once at the old scroll position and then jumped — and
  // everything in it, **Use this one** included, moved under whatever was
  // already reaching for it. Mobile Safari caught it in the gate, missing the
  // button and leaving the portrait unattached. Same reason `useReaderSettings`
  // applies its theme in a layout effect.
  useLayoutEffect(() => {
    const el = body.current;
    if (el && candidates.length > 0) el.scrollTop = 0;
  }, [candidates.length]);

  const [costBefore, costAfter] = aroundAmount(t.images.costEach);
  const busy =
    describe.isPending || draw.isPending || upload.isPending || place.isPending || crop.isPending;
  const ready = description.trim() !== "";
  // Nothing described and no turn taken yet: the box is the author's seed and
  // its send button is Suggest (ADR-0059). Keyed to the state of the sheet
  // rather than to whether the box has anything in it, because the call is the
  // same call either way — a seed is the first instruction turn.
  const seeding = !ready && earlier.length === 0;

  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid="image-sheet"
      /* Above the studio's own bars, and a fixed height so a picture arriving
         does not push the controls out from under the thumb. */
      className="fixed inset-x-0 bottom-0 z-40 flex h-[90dvh] flex-col rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col">
        <SheetHeader title={title} onClose={onClose} closeTestID="image-close" />

        {subject && (
          /* Where the picture is going. dir="auto" on the authored line and
             nothing inside it. */
          <p
            dir="auto"
            data-testid="image-passage"
            className="mx-4 mt-3 line-clamp-2 rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5 text-xs leading-6 text-ink-soft"
          >
            {subject}
          </p>
        )}

        {canDescribe && (
          <div className="mx-4 mt-3 flex rounded-control bg-sunk p-0.5 text-sm">
            <Tab
              active={tab === "describe"}
              testid="tab-describe"
              onClick={() => setTab("describe")}
            >
              {t.images.describeTab}
            </Tab>
            <Tab active={tab === "upload"} testid="tab-upload" onClick={() => setTab("upload")}>
              {t.images.uploadTab}
            </Tab>
          </div>
        )}

        <div ref={body} data-testid="image-sheet-body" className="flex-1 overflow-y-auto px-4 py-4">
          {cropping ? (
            <Cropper
              src={cropping.of === "candidate" ? cropping.candidate.url : (current?.url ?? "")}
              shape={KINDS[kind].cropShape}
              lockedNote={KINDS[kind].lockedNote?.(t)}
              applying={crop.isPending}
              error={crop.error}
              onApply={(region) => crop.mutate(region)}
              onCancel={() => {
                crop.reset();
                setCropping(null);
              }}
            />
          ) : (
            <>
              {/* The picture first once there is one (requirement 0030). It was
                  under the brief, the passage, the tabs and the picture in
                  force, which on a phone left a third of the height for the
                  one thing being judged. */}
              {candidates.length > 0 && (
                <Gallery
                  candidates={candidates}
                  chosen={chosen}
                  onChoose={setChosen}
                  onUse={(candidate) => place.mutate(candidate)}
                  onCrop={(candidate) => setCropping({ of: "candidate", candidate })}
                  placing={place.isPending}
                />
              )}

              {draw.isPending && (
                <p
                  data-testid="image-drawing"
                  aria-live="polite"
                  className="mt-4 flex items-center gap-2 text-sm text-muted"
                >
                  <span className="size-3 animate-pulse rounded-full bg-sage" aria-hidden="true" />
                  {t.images.drawing}
                </p>
              )}

              {current && (
                <div className="mb-4 flex flex-wrap items-start gap-4">
                  <figure data-testid="image-current">
                    <img
                      src={current.url}
                      alt=""
                      width={current.width}
                      height={current.height}
                      className="max-h-40 w-auto rounded-card border border-line"
                    />
                    <figcaption className="mt-1 flex items-center gap-3 text-[0.7rem] text-muted">
                      {t.images.current}
                      {current.object_key && (
                        <button
                          type="button"
                          data-testid="image-current-crop"
                          disabled={busy}
                          onClick={() => setCropping({ of: "current" })}
                          className="font-medium text-ink-soft underline-offset-2 hover:underline disabled:opacity-40"
                        >
                          {t.crop.crop}
                        </button>
                      )}
                    </figcaption>
                  </figure>
                  {live && (
                    <figure data-testid="image-current-live">
                      {live}
                      <figcaption className="mt-1 text-[0.7rem] text-muted">
                        {t.live.heading} · {t.live.note}
                      </figcaption>
                    </figure>
                  )}
                </div>
              )}
              {tab === "describe" ? (
                <Describe
                  description={description}
                  onDescription={setDescription}
                  writing={describe.isPending}
                  empty={seeding}
                  updated={updated}
                  open={briefOpen}
                  onOpen={setBriefOpen}
                  shapePicker={
                    KINDS[kind].choosesShape && candidates.length > 0 ? (
                      <ShapeChips shape={shape} onShape={setShape} disabled={busy} />
                    ) : null
                  }
                  kind={kind}
                  cast={hasCast ? members : null}
                  roster={offered}
                  rosterLoaded={!!people}
                  onCast={(ids) => {
                    setProblem(null);
                    chooseCast(ids);
                  }}
                  needsFaces={needsFaces}
                  charactersHref={charactersHref}
                  continuity={
                    hasContinuity ? (
                      <ContinuityPicker
                        pictures={earlierPictures}
                        chosen={liveBeside}
                        onChosen={setBeside}
                      />
                    ) : null
                  }
                />
              ) : (
                <Upload onFile={(file) => upload.mutate(file)} uploading={upload.isPending} />
              )}

              {problem && (
                <p
                  role="alert"
                  data-testid="image-error"
                  className="mt-4 text-sm text-red-600 dark:text-red-400"
                >
                  {problem}
                  {problemIsCast && charactersHref && (
                    <>
                      {" "}
                      <CharactersLink href={charactersHref} />
                    </>
                  )}
                </p>
              )}
            </>
          )}
        </div>

        {tab === "describe" && !cropping && (
          <div className="border-t border-line px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {/* The shape lives here until the first picture exists and inside
                the folded brief afterwards (requirement 0030): before a draw it
                is a choice under the thumb, after one it is a row of chips
                between the author and their picture. */}
            <div className="flex flex-col gap-1.5">
              {KINDS[kind].choosesShape && candidates.length === 0 && (
                <ShapeChips shape={shape} onShape={setShape} disabled={busy} />
              )}
              {/* **The quality stays put when the brief folds**, unlike the
                  shape. The loop this feature is for is: draw cheap ones until
                  the words are right, then buy the keeper on the good model —
                  so the choice has to be under the thumb at the press that
                  buys it, not behind a fold (ADR-0086). */}
              {prices.length > 1 && (
                <QualityChips
                  quality={quality}
                  onQuality={setQuality}
                  prices={prices}
                  disabled={busy}
                />
              )}
            </div>

            <form
              className="mt-2 flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                sendInstruction();
              }}
            >
              <label className="min-w-0 flex-1">
                <span className="sr-only">{t.images.yourInstruction}</span>
                <textarea
                  dir="auto"
                  rows={1}
                  value={instruction}
                  maxLength={1000}
                  enterKeyHint="send"
                  data-testid="image-instruction"
                  placeholder={ready ? t.images.placeholderFollowUp : KINDS[kind].placeholder(t)}
                  onChange={(e) => setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      sendInstruction();
                    }
                  }}
                  /* text-base: iOS zooms into any input under 16px on focus. */
                  className="block max-h-24 w-full resize-none rounded-control border border-line bg-surface px-3 py-2 text-base [field-sizing:content]"
                />
              </label>
              <button
                type="submit"
                /* Empty and nothing described yet, this button is Suggest —
                   the same call, which is why it is the same control
                   (ADR-0059). Afterwards it is the *words only* half of the
                   pair: the primary button below does words-then-picture, and
                   this one stops at the words. Named for what it will do, so
                   nobody — reading it or hearing it — is left with a bare
                   arrow that "does sth" (requirement 0030). */
                aria-label={
                  seeding && instruction.trim() === ""
                    ? KINDS[kind].suggest(t)
                    : t.images.updateWords
                }
                title={
                  seeding && instruction.trim() === ""
                    ? KINDS[kind].suggest(t)
                    : t.images.updateWords
                }
                data-testid={seeding ? "image-suggest" : "image-describe"}
                disabled={busy}
                className="grid size-10 shrink-0 place-items-center rounded-full border border-line text-ink-soft transition-colors hover:bg-sunk disabled:opacity-40"
              >
                <SendIcon />
              </button>
            </form>

            {/* The words changed, and saying so is the whole of the owner's
                first complaint: the new description lands above the fold on a
                phone, so a turn that worked looked exactly like one that did
                not. aria-live, because a screen reader has even less chance of
                noticing a textarea scroll past. */}
            {describe.isPending ? (
              <p
                data-testid="image-updating"
                aria-live="polite"
                className="mt-1.5 text-center text-[0.7rem] text-muted"
              >
                {t.images.updating}
              </p>
            ) : (
              updated && (
                <p
                  data-testid="image-updated"
                  aria-live="polite"
                  className="mt-1.5 text-center text-[0.7rem] text-sage-deep"
                >
                  {t.images.descriptionUpdated}
                </p>
              )
            )}

            {/* One primary button, whose words are what pressing it will do
                (requirement 0030). With something in the box it updates the
                description and draws the result; empty, it draws what is
                already there. It is never possible to press this and wonder
                whether the change in the box was used. */}
            <button
              type="button"
              data-testid="image-draw"
              onClick={() => void drawNow()}
              disabled={busy || (!ready && instruction.trim() === "")}
              className="mt-2 w-full rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {instruction.trim() !== ""
                ? t.images.updateAndDraw
                : candidates.length > 0
                  ? t.images.drawAgain
                  : t.images.draw}
              {/* The price on the button, before it is pressed. Only the
                  amount is isolated left to right; the separator and the words
                  follow the interface (05 §Web client). */}
              <span data-testid="image-draw-price" className="font-normal opacity-80">
                {t.book.sep}
                {costBefore}
                <bdi dir="ltr">{chosenPrice?.display ?? ""}</bdi>
                {costAfter}
              </span>
            </button>

            {spentMicros > 0 && (
              <p data-testid="image-spent" className="mt-1.5 text-center text-[0.7rem] text-muted">
                {t.images.spentHere(formatMicros(spentMicros))}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Tab({
  active,
  testid,
  onClick,
  children,
}: {
  active: boolean;
  testid: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={`flex-1 rounded-[0.6rem] px-3 py-1.5 transition-colors ${
        active ? "bg-surface font-medium text-ink shadow-sm" : "text-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Describe is the brief: an editable textarea, because the words in it are
 * what the picture model is sent verbatim.
 */
function Describe({
  description,
  onDescription,
  writing,
  empty,
  updated,
  open,
  onOpen,
  shapePicker,
  kind,
  cast,
  roster,
  rosterLoaded,
  onCast,
  needsFaces,
  charactersHref,
  continuity,
}: {
  description: string;
  onDescription: (text: string) => void;
  writing: boolean;
  empty: boolean;
  /** Whether the last turn rewrote these words, so the change can be shown. */
  updated: boolean;
  /** Whether the brief is unfolded. Folded once a picture exists (0030). */
  open: boolean;
  onOpen: (open: boolean) => void;
  /** The shape chips, which fold away with the brief. Null for a fixed shape. */
  shapePicker: React.ReactNode;
  kind: PictureKind;
  /** Who is in the picture (ADR-0047), or null for a kind without a cast. */
  cast: CastMember[] | null;
  /** The book's characters, to add from. */
  roster: { id: string; name: string }[];
  rosterLoaded: boolean;
  onCast: (ids: string[]) => void;
  /** Those in the cast with no portrait yet (ADR-0044). */
  needsFaces: string[];
  charactersHref?: string;
  /** The earlier-pictures row (ADR-0072), or null for a kind without one. */
  continuity: React.ReactNode;
}) {
  const t = useT();
  // The one refusal worth pre-empting: a picture naming somebody who has no
  // face cannot be drawn at all, and finding that out costs a turn.
  const facesNote = needsFaces.length > 0 && (
    <p
      role="status"
      data-testid="image-needs-faces"
      className="mt-1.5 rounded-control bg-amber-50 px-2 py-1.5 text-[0.7rem] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
    >
      {t.images.needsFaces(needsFaces)}
      {charactersHref && (
        <>
          {" "}
          <CharactersLink href={charactersHref} />
        </>
      )}
    </p>
  );
  if (empty && !writing) {
    return (
      <div className="py-6">
        <p data-testid="image-empty" className="mb-4 text-center text-sm leading-6 text-muted">
          {KINDS[kind].empty(t)}
        </p>
        {/* Who should be in it can be said before any words exist (ADR-0053):
            the first description is then asked to show them. */}
        {cast && (
          <div className="mb-4">
            <CastPicker
              cast={cast}
              roster={roster}
              onCast={onCast}
              charactersHref={rosterLoaded ? charactersHref : undefined}
            />
            {facesNote}
            {continuity}
          </div>
        )}
        {/* No Suggest button here on purpose (ADR-0059). The box at the foot
            of the sheet is the first turn: what is typed into it is the
            author's seed, and sending it empty asks for a suggestion. A
            button above the box made the box look like the follow-up chat it
            becomes later, and the seed — the thing the author actually wants
            to say first — went unnoticed. */}
        <p data-testid="image-seed-hint" className="text-center text-sm text-muted">
          {t.images.seedHint}
        </p>
      </div>
    );
  }
  return (
    <div className="mt-4">
      {/* Folded once a picture exists (requirement 0030): a line of the words,
          tappable, so the picture above gets the height. A summary rather than
          nothing, because the author still has to see at a glance *which*
          brief is about to be drawn again.

          The cast and the faces warning below never fold. They are one compact
          row, and they are the thing most often changed between two draws —
          folding them would hide the control the next press depends on. */}
      {open ? (
        <>
          <label className="block">
            <span className="mb-1 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink-soft">{t.images.description}</span>
              <button
                type="button"
                data-testid="image-brief-toggle"
                aria-expanded={true}
                onClick={() => onOpen(false)}
                className="text-[0.7rem] text-sage-deep underline-offset-2 hover:underline"
              >
                {t.images.hideBrief}
              </button>
            </span>
            <textarea
              dir="auto"
              rows={5}
              value={description}
              maxLength={2000}
              disabled={writing}
              data-testid="image-description"
              onChange={(e) => onDescription(e.target.value)}
              /* Ringed after a turn rewrote it, so the change shows where it
                 happened and not only in the line under the box. */
              className={`block max-h-64 w-full resize-y rounded-card border bg-ground px-3 py-2.5 text-base leading-7 transition-colors disabled:opacity-60 [field-sizing:content] ${
                updated ? "border-sage" : "border-line"
              }`}
            />
          </label>
          <p className="mt-1 text-[0.7rem] text-muted">
            {writing ? t.images.writing : t.images.descriptionHint}
          </p>
          {shapePicker && <div className="mt-2">{shapePicker}</div>}
        </>
      ) : (
        <button
          type="button"
          data-testid="image-brief-toggle"
          aria-expanded={false}
          onClick={() => onOpen(true)}
          className={`flex w-full items-start gap-2 rounded-control border px-3 py-2 text-start transition-colors hover:bg-sunk ${
            updated ? "border-sage bg-sage-soft" : "border-line"
          }`}
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[0.7rem] font-medium text-ink-soft">
              {t.images.description}
            </span>
            <span dir="auto" className="line-clamp-2 block text-xs text-muted">
              {description}
            </span>
          </span>
          <span className="shrink-0 text-[0.7rem] text-sage-deep">{t.images.showBrief}</span>
        </button>
      )}
      {cast && !writing && (
        <CastPicker
          cast={cast}
          roster={roster}
          onCast={onCast}
          charactersHref={rosterLoaded ? charactersHref : undefined}
        />
      )}
      {facesNote}
      {!writing && continuity}
    </div>
  );
}

/** The most earlier pictures one draw carries; the server refuses more. */
const MAX_BESIDE = 2;

/**
 * ContinuityPicker is the earlier pictures this one is drawn beside
 * (ADR-0072, requirement 0029): the place, not the people.
 *
 * Thumbnails rather than a list of descriptions, because what an author is
 * choosing is a *look* — a tent, a coastline, a hall — and they recognise it
 * in a picture and not in sixty words. The description goes along with it
 * anyway; the author never picks a prompt.
 *
 * Folded until asked for, like the cast's own Add row: a book of forty
 * pictures would otherwise be a wall of thumbnails under every new one.
 */
function ContinuityPicker({
  pictures,
  chosen,
  onChosen,
}: {
  pictures: BookPicture[];
  chosen: string[];
  onChosen: (ids: string[]) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (pictures.length === 0) return null;
  const picked = pictures.filter((p) => chosen.includes(p.id));
  const full = chosen.length >= MAX_BESIDE;
  const toggle = (id: string) =>
    onChosen(
      chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id].slice(-MAX_BESIDE),
    );
  return (
    <div data-testid="image-continuity" className="mt-2 text-[0.7rem] text-muted">
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{t.images.besideLabel}</span>
        {picked.length === 0 && <span data-testid="image-beside-none">{t.images.besideNone}</span>}
        {picked.map((picture) => (
          <button
            key={picture.id}
            type="button"
            data-testid="image-beside-chosen"
            aria-label={t.images.besideRemove(picture.chapter_title)}
            onClick={() => toggle(picture.id)}
            className="inline-flex items-center gap-1 rounded-full border border-sage bg-sage-soft py-0.5 ps-0.5 pe-2 font-medium text-sage-deep"
          >
            <img src={picture.url} alt="" className="size-6 rounded-full object-cover" />
            <span aria-hidden="true">×</span>
          </button>
        ))}
        <button
          type="button"
          data-testid="image-beside-open"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          className="inline-flex items-center rounded-full border border-dashed border-line px-2.5 py-1 text-ink transition-colors hover:border-sage"
        >
          {open ? t.images.castDone : `+ ${t.images.besideAdd}`}
        </button>
      </div>
      {open && (
        <>
          <p className="mt-1.5">{t.images.besideWhy}</p>
          <ul
            data-testid="image-beside-choices"
            className="mt-1.5 flex gap-2 overflow-x-auto rounded-control bg-sunk p-1.5"
          >
            {pictures.map((picture) => {
              const on = chosen.includes(picture.id);
              return (
                <li key={picture.id} className="shrink-0">
                  <button
                    type="button"
                    data-testid="image-beside-choice"
                    aria-pressed={on}
                    /* A full row still lets the ticked ones be unticked, and
                       ticking a third replaces the oldest rather than doing
                       nothing — a disabled grid with no explanation is how an
                       author decides the feature is broken. */
                    title={picture.prompt || picture.chapter_title}
                    onClick={() => toggle(picture.id)}
                    className={`block overflow-hidden rounded-control border-2 transition-colors ${
                      on ? "border-sage" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={picture.url} alt="" className="size-16 object-cover" />
                  </button>
                </li>
              );
            })}
          </ul>
          {full && <p className="mt-1">{t.images.besideFull(MAX_BESIDE)}</p>}
        </>
      )}
    </div>
  );
}

interface CastMember {
  id: string;
  name: string;
}

/**
 * CastPicker is who the picture is drawn from (ADR-0047): the describe turn's
 * answer, shown so the author can take somebody out or add somebody the brief
 * missed. Editing the words leaves it alone.
 */
function CastPicker({
  cast,
  roster,
  onCast,
  charactersHref,
}: {
  cast: CastMember[];
  roster: { id: string; name: string }[];
  onCast: (ids: string[]) => void;
  charactersHref?: string;
}) {
  const t = useT();
  // The rest of the book's characters stay folded away until asked for: a
  // book of forty would otherwise be a wall of names under every picture.
  const [adding, setAdding] = useState(false);
  const ids = cast.map((m) => m.id);
  // One shape per character: a character in the picture, as themselves or in
  // a form, is not offered again in another (ADR-0159).
  const whose = (key: string) => key.split("/")[0];
  const addable = roster.filter((c) => !ids.some((id) => whose(id) === whose(c.id)));
  const full = cast.length >= MAX_CAST;
  const chip =
    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors";
  return (
    <div data-testid="image-cast" className="mt-2 text-[0.7rem] text-muted">
      <div className="flex flex-wrap items-center gap-1.5">
        <span>{t.images.castLabel}</span>
        {cast.length === 0 && !(roster.length === 0 && charactersHref) && (
          <span data-testid="image-cast-empty">{t.images.drawnFromNobody}</span>
        )}
        {cast.map((member) => (
          <button
            key={member.id}
            type="button"
            data-testid="image-cast-member"
            aria-label={t.images.castRemove(member.name)}
            onClick={() => onCast(ids.filter((id) => id !== member.id))}
            className={`${chip} border-sage bg-sage-soft font-medium text-sage-deep`}
          >
            <span dir="auto">{member.name}</span>
            <span aria-hidden="true">×</span>
          </button>
        ))}
        {addable.length > 0 && (
          <button
            type="button"
            data-testid="image-cast-add"
            aria-expanded={adding}
            onClick={() => setAdding((open) => !open)}
            className={`${chip} border-dashed border-line text-ink hover:border-sage`}
          >
            {adding ? t.images.castDone : `+ ${t.images.castAdd}`}
          </button>
        )}
        {/* A book nobody has listed characters for has nobody to add: say
            where they come from rather than showing nothing (ADR-0053). */}
        {roster.length === 0 && cast.length === 0 && charactersHref && (
          <span data-testid="image-cast-none">
            {t.images.castNoneYet} <CharactersLink href={charactersHref} />
          </span>
        )}
      </div>
      {adding && addable.length > 0 && (
        <div
          data-testid="image-cast-choices"
          className="mt-1.5 flex flex-wrap gap-1.5 rounded-control bg-sunk p-1.5"
        >
          {addable.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid="image-cast-choice"
              disabled={full}
              onClick={() => {
                onCast([...ids, c.id]);
                // Nobody left to add: fold the row, so a later Add opens it.
                if (addable.length === 1) setAdding(false);
              }}
              className={`${chip} border-line bg-surface text-ink-soft hover:text-ink disabled:opacity-40`}
            >
              <span dir="auto">{c.name}</span>
            </button>
          ))}
          {full && <span className="self-center">{t.images.castFull}</span>}
        </div>
      )}
    </div>
  );
}

/** The most portraits a picture is drawn from; the server refuses more. */
const MAX_CAST = 4;

/** The way to the book's characters, from a refusal that is remedied there. */
function CharactersLink({ href }: { href: string }) {
  const t = useT();
  return (
    <Link
      to={href}
      data-testid="image-characters-link"
      className="font-medium underline underline-offset-2"
    >
      {t.portraits.goToCast}
    </Link>
  );
}

/**
 * ShapeChips is the aspect ratio, for the kinds that choose one: a scene does,
 * a portrait is always square and a cover always upright.
 *
 * It folds away with the brief, because a shape is chosen before the first
 * picture and almost never after.
 */
function ShapeChips({
  shape,
  onShape,
  disabled,
}: {
  shape: ImageShape;
  onShape: (shape: ImageShape) => void;
  disabled: boolean;
}) {
  const t = useT();
  return (
    <ChipRow
      legend={t.images.shape}
      value={shape}
      onChange={onShape}
      disabled={disabled}
      options={IMAGE_SHAPES.map((option) => ({
        value: option,
        label: t.images.shapeNames[option] ?? option,
        testID: `image-shape-${option.replace(":", "-")}`,
      }))}
    />
  );
}

/**
 * QualityChips is which model draws it (ADR-0086), and what that costs.
 *
 * The price is on the chip as well as on the button: the choice is entirely
 * about money, and the two amounts side by side are the comparison somebody is
 * actually making. The server sends the list, so a client never invents a
 * quality and never formats an amount.
 */
function QualityChips({
  quality,
  onQuality,
  prices,
  disabled,
}: {
  quality: ImageQuality;
  onQuality: (quality: ImageQuality) => void;
  prices: DrawPrice[];
  disabled: boolean;
}) {
  const t = useT();
  return (
    <ChipRow
      legend={t.images.quality}
      value={quality}
      onChange={onQuality}
      disabled={disabled}
      options={prices.map((price) => ({
        value: price.quality,
        label: t.images.qualityNames[price.quality] ?? price.quality,
        note: price.display,
        testID: `image-quality-${price.quality}`,
      }))}
    />
  );
}

/**
 * Gallery is what has been drawn: the chosen one large enough to judge, the
 * rest as a strip to compare against. Judging a picture from a thumbnail is
 * how the wrong one gets kept.
 */
function Gallery({
  candidates,
  chosen,
  onChoose,
  onUse,
  onCrop,
  placing,
}: {
  candidates: ImageCandidate[];
  chosen: ImageCandidate | null;
  onChoose: (candidate: ImageCandidate) => void;
  onUse: (candidate: ImageCandidate) => void;
  onCrop: (candidate: ImageCandidate) => void;
  placing: boolean;
}) {
  const t = useT();
  const showing = chosen ?? candidates[candidates.length - 1]!;
  return (
    <section data-testid="image-gallery">
      <h3 className="mb-2 text-xs font-medium text-ink-soft">{t.images.candidates}</h3>
      <img
        src={showing.url}
        alt=""
        width={showing.width}
        height={showing.height}
        data-testid="image-preview"
        /* Bounded by the sheet rather than by its own aspect ratio: a 3:4
           cover at full width is taller than a phone's sheet, which is how the
           picture ended up being scrolled to (requirement 0030). */
        className="mx-auto h-auto max-h-[58dvh] w-auto max-w-full rounded-card bg-sunk object-contain"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="image-crop"
          disabled={placing}
          onClick={() => onCrop(showing)}
          className="rounded-control border border-line px-4 py-2.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {t.crop.crop}
        </button>
        <button
          type="button"
          data-testid="image-use"
          disabled={placing}
          onClick={() => onUse(showing)}
          className="flex-1 rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
        >
          {placing ? t.images.placing : t.images.use}
        </button>
      </div>

      {candidates.length > 1 && (
        <ul className="mt-2 flex gap-2 overflow-x-auto pb-1">
          {candidates.map((candidate, i) => (
            <li key={candidate.object_key} className="shrink-0">
              <button
                type="button"
                data-testid="image-thumb"
                aria-pressed={candidate.object_key === showing.object_key}
                aria-label={`${i + 1}`}
                onClick={() => onChoose(candidate)}
                className={`block overflow-hidden rounded-control border-2 transition-colors ${
                  candidate.object_key === showing.object_key
                    ? "border-sage"
                    : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={candidate.url} alt="" className="size-16 object-cover" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Upload takes a picture from the author's device, by picker or by drop —
 * the same two ways a chapter's text arrives (`TextFileDrop`).
 */
function Upload({ onFile, uploading }: { onFile: (file: Blob) => void; uploading: boolean }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      }}
      data-testid="image-drop"
      className={`rounded-card border border-dashed px-4 py-8 text-center transition-colors ${
        over ? "border-sage bg-sage-soft" : "border-line-strong"
      }`}
    >
      <button
        type="button"
        disabled={uploading}
        data-testid="image-choose"
        onClick={() => input.current?.click()}
        className="rounded-control border border-line px-3 py-1.5 text-sm font-medium transition-colors hover:bg-sunk disabled:opacity-40"
      >
        {uploading ? t.images.uploading : t.images.chooseFile}
      </button>
      {/* The hidden input is the real control; a bare file input cannot be
          styled to match anything. `capture` is deliberately absent, so a
          phone offers the camera *and* the library rather than forcing one. */}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        data-testid="image-file"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          // Cleared so choosing the same file twice fires change again.
          e.target.value = "";
        }}
      />
      <p className="mt-2 text-xs text-muted">{t.images.orDropImage}</p>
    </div>
  );
}
